import React, { CSSProperties, FC, useId } from 'react'
import './PlugMark.css'

/** gradient: the heritage sweep. auto: sweep on night, white on flux (follows --plug-mark). */
export type PlugMarkVariant = 'gradient' | 'white' | 'black' | 'auto'
/** float: float + blink (idle). still: blink only (waiting on the user). none: static. */
export type PlugMarkMotion = 'float' | 'still' | 'none'

export interface PlugMarkProps {
  variant?: PlugMarkVariant
  size?: number // rendered height in px; width follows the 202:232 ratio
  motion?: PlugMarkMotion
  enter?: boolean // play the entrance once on mount: scale .92 → 1, fade in, 420ms
  title?: string
  className?: string
  style?: CSSProperties
  x?: number // placement when nested inside another SVG
  y?: number
  width?: number
  height?: number
}

const BODY =
  'M0 90C0 34.59 38.81 0 101 0C163.19 0 202 34.59 202 90C202 145.41 163.19 180 101 180C88.32 180 76.61 178.56 66 175.8Q54 171.94 54 183.94V202A30 30 0 0 1 24 232H0Z'

const SWEEP: [string, string][] = [
  ['0', '#6EE2A8'],
  ['0.28', '#22C6E3'],
  ['0.52', '#7A5BF0'],
  ['0.74', '#D84CC2'],
  ['0.93', '#F4D12A'],
  ['1', '#F4D12A'],
]

const FILL = { white: '#FFFFFF', black: '#08061A' } as const

export const PlugMark: FC<PlugMarkProps> = ({
  variant = 'auto',
  size = 96,
  motion = 'none',
  enter = false,
  title = 'Plug',
  className,
  style,
  x,
  y,
  width,
  height,
}) => {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const gradientId = `plug-sweep-${id}`
  const maskId = `plug-eyes-${id}`
  const blink = motion !== 'none' ? 'plug-blink' : undefined
  const fill =
    variant === 'gradient' ? `url(#${gradientId})` : variant === 'auto' ? undefined : FILL[variant]

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 202 232"
      width={width ?? (size * 202) / 232}
      height={height ?? size}
      x={x}
      y={y}
      overflow="visible"
      role="img"
      aria-label={title}
      className={['plug-mark', variant === 'auto' && 'plug-mark--auto', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
    >
      <defs>
        {(variant === 'gradient' || variant === 'auto') && (
          <linearGradient
            id={gradientId}
            x1="150"
            y1="0"
            x2="18"
            y2="232"
            gradientUnits="userSpaceOnUse"
          >
            {SWEEP.map(([offset, color]) => (
              <stop key={offset} offset={offset} stopColor={color} />
            ))}
          </linearGradient>
        )}
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="202" height="232">
          <rect width="202" height="232" fill="#fff" />
          <g transform="translate(75.1 90) rotate(35)">
            <ellipse className={blink} rx="15.5" ry="30" fill="#000" />
          </g>
          <g transform="translate(130.5 90) rotate(35)">
            <ellipse className={blink} rx="15.5" ry="30" fill="#000" />
          </g>
        </mask>
      </defs>
      <g className={enter ? 'plug-mark__enter' : undefined}>
        <g className={motion === 'float' ? 'plug-float' : undefined}>
          <path
            d={BODY}
            fill={fill}
            style={
              variant === 'auto'
                ? { fill: `var(--plug-mark-fill, url(#${gradientId}))` }
                : undefined
            }
            mask={`url(#${maskId})`}
          />
        </g>
      </g>
    </svg>
  )
}
